import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Divider,
  Chip,
  Fab,
  FormHelperText,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { Alert, AlertTitle } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchField from "react-search-field";

export default function Applications(props) {
  const { OSTYPE, appsetFormFields, appfieldIndex, value } = props;
  const [customapps, setCustomapps] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedApps, setSelectedApps] = useState({});
  const [appVersions, setAppVersions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState({});

  // Initialize selected apps from props value when opening
  useEffect(() => {
    if (open && value && value[appfieldIndex]) {
      const initialSelectedApps = {};
      value[appfieldIndex].forEach(appWithVersion => {
        const [appName, version] = appWithVersion.split('-');
        initialSelectedApps[appName] = { version };
      });
      setSelectedApps(initialSelectedApps);
    }
  }, [open, value, appfieldIndex]);

  const fetchCustomApps = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customsystemtools/${OSTYPE}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      const data = await response.json();
      setCustomapps(data);
    } catch (err) {
      console.error("Failed to fetch custom apps:", err);
      setError("Failed to load system tools");
    }
  };

  const fetchAppVersions = async (appName) => {
    try {
      setLoadingVersions(prev => ({ ...prev, [appName]: true }));
      const apipostval = `${appName}-${OSTYPE}`;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/customversionlist/${apipostval}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      const versions = await response.json();
      setAppVersions(prev => ({
        ...prev,
        [appName]: versions.split(',')
      }));
    } catch (err) {
      console.error(`Failed to fetch versions for ${appName}:`, err);
      setError(`Failed to load versions for ${appName}`);
    } finally {
      setLoadingVersions(prev => ({ ...prev, [appName]: false }));
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    await fetchCustomApps();
    
    // Pre-fetch versions for already selected apps
    if (value && value[appfieldIndex]) {
      const appsToFetch = value[appfieldIndex].map(appWithVersion => {
        return appWithVersion.split('-')[0];
      });
      
      await Promise.all(appsToFetch.map(appName => {
        if (!appVersions[appName]) {
          return fetchAppVersions(appName);
        }
        return Promise.resolve();
      }));
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAppToggle = async (appName) => {
    if (selectedApps[appName]) {
      // If app is already selected, deselect it
      setSelectedApps(prev => {
        const newState = { ...prev };
        delete newState[appName];
        return newState;
      });
    } else {
      // If app is being selected, fetch its versions if not already loaded
      setSelectedApps(prev => ({
        ...prev,
        [appName]: { version: "" }
      }));
      if (!appVersions[appName]) {
        await fetchAppVersions(appName);
      }
    }
  };

  const handleVersionChange = (appName, version) => {
    setSelectedApps(prev => ({
      ...prev,
      [appName]: { ...prev[appName], version }
    }));
  };

  const handleSubmit = () => {
    // Validate all selected apps have versions
    const hasMissingVersions = Object.entries(selectedApps).some(
      ([, details]) => !details.version
    );

    if (hasMissingVersions) {
      setError("Please select versions for all chosen applications");
      return;
    }

    // Prepare the data to save
    const selectedAppValues = Object.entries(selectedApps)
      .map(([name, details]) => `${name.trim()}-${details.version.trim()}`);

    appsetFormFields(prev => ({
      ...prev,
      [appfieldIndex]: selectedAppValues,
    }));

    handleClose();
  };

  const filteredApps = customapps.filter(app =>
    app.Title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Fab 
        variant="extended" 
        size="small" 
        color="primary"
        sx={{ borderRadius: '16px' }}
      >
        <Chip
          icon={<AddIcon />}
          label="System Tools"
          clickable
          color="primary"
          onClick={handleOpen}
        />
      </Fab>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>System Tools</DialogTitle>
        
        {error && (
          <Alert severity="warning">
            <AlertTitle>Warning</AlertTitle>
            {error}
          </Alert>
        )}

        <DialogContent dividers>
          <SearchField
            placeholder="Search..."
            onChange={setSearchTerm}
            classNames="test-class"
          />
          
          <DialogContentText>
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <Grid container spacing={0} sx={{ mt: 3 }}>
                    <Grid item xs={8}>
                      <Typography variant="h8" gutterBottom>
                        System Tools
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h8" gutterBottom>
                        Version
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider />
                  
                  {filteredApps.map((app) => (
                    <Grid container spacing={0} key={app.Name} sx={{ mt: 3 }}>
                      <Grid item xs={8}>
                        <Checkbox
                          checked={!!selectedApps[app.Name]}
                          onChange={() => handleAppToggle(app.Name)}
                          value={app.Name}
                        />
                        <Typography variant="h8" gutterBottom>
                          {app.Title}
                        </Typography>
                        <FormHelperText sx={{ ml: 5 }}>
                          min req: {app.requirements}
                        </FormHelperText>
                      </Grid>
                      <Grid item xs={4}>
                        {selectedApps[app.Name] && (
                          <TextField
                            fullWidth
                            select
                            SelectProps={{ native: true }}
                            value={selectedApps[app.Name].version || ""}
                            onChange={(e) => 
                              handleVersionChange(app.Name, e.target.value)
                            }
                            disabled={loadingVersions[app.Name]}
                          >
                            <option value="">-- Select Version --</option>
                            {loadingVersions[app.Name] ? (
                              <option value="">Loading versions...</option>
                            ) : (
                              appVersions[app.Name]?.map((version) => (
                                <option key={version} value={version}>
                                  {version}
                                </option>
                              ))
                            )}
                          </TextField>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                
                <Grid item xs={4}>
                  <Box
                    sx={{
                      fontStyle: "italic",
                      height: "100%",
                      backgroundColor: "whitesmoke",
                      p: 2,
                    }}
                    border={1}
                  >
                    <strong>Chosen applications:</strong>
                    {Object.entries(selectedApps)
                      .map(([name, details]) => (
                        <li key={name} style={{ textAlign: "center" }}>
                          {`${name}-${details.version || "<Version>"}`}
                        </li>
                      ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContentText>
        </DialogContent>
        
        <DialogActions>
          <Tooltip title="Close dialog without saving">
            <Button onClick={handleClose} variant="contained" color="primary" sx={{ borderRadius: '50px' }}>
              Close
            </Button>
          </Tooltip>
          <Tooltip title="Save changes">
            <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ borderRadius: '50px' }}>
              Save
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
}